import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./CashlessModal.scss";
import { CashlessCardQrPreview } from "./CashlessCardQrModal";

export function AddEditCardModal({ show, onHide, onSave, cardToEdit = null }) {
  const { t } = useTranslation("cashless");
  const [cardId, setCardId] = useState("");
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [holder, setHolder] = useState("");
  const [showQrPreview, setShowQrPreview] = useState(false);

  useEffect(() => {
    if (cardToEdit) {
      setCardId(cardToEdit.id || "");
      setBalance(cardToEdit.balance?.toString() || "");
      setHolder(cardToEdit.holder ?? "");
      setShowQrPreview(Boolean(cardToEdit.id));
    } else {
      setCardId("");
      setBalance(0);
      setHolder("");
      setShowQrPreview(false);
    }
  }, [cardToEdit]);

  const handleSave = async () => {
    if (!cardId.trim()) return;

    setLoading(true);
    try {
      const cardData = {
        id: cardId.trim().toUpperCase(),
        balance: parseFloat(balance) || 0,
        holder: holder,
      };

      await onSave(cardData);
      handleCancel();
    } catch (error) {
      if (error.code !== "cashless-card-id-already-exists") console.error("Error saving card:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCardId("");
    setBalance("");
    setHolder("");
    setShowQrPreview(false);
    onHide();
  };

  const generateCardId = () => {
    const randomPart = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    setCardId(`CL-${randomPart}`.toUpperCase());
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block cashless-modal">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{cardToEdit ? t("editCard") : t("addCard")}</h5>
            <button type="button" className="btn-close" onClick={handleCancel} />
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <label htmlFor="cardId" className="form-label">
                {t("cardId", "Card ID")} <span className="text-danger">*</span>
              </label>
              <div className="cashless-card-id-input">
                <input
                  type="text"
                  className="form-control"
                  id="cardId"
                  value={cardId}
                  onChange={(e) => setCardId(e.target.value)}
                  placeholder={t("enterCardId")}
                  disabled={Boolean(cardToEdit)}
                />
                {!cardToEdit && (
                  <button type="button" className="btn btn-secondary" onClick={generateCardId}>
                    {t("generateCardId")}
                  </button>
                )}
              </div>
              {!cardToEdit && cardId.trim() && !showQrPreview && (
                <button
                  type="button"
                  className="btn btn-outline-light cashless-card-qr-button"
                  onClick={() => setShowQrPreview(true)}
                >
                  {t("generateQrCode")}
                </button>
              )}
            </div>

            {showQrPreview && <CashlessCardQrPreview cardId={cardId} />}

            <div className="mb-3">
              <label htmlFor="holder" className="form-label">
                {t("holder")}
              </label>
              <input
                className="form-control"
                id="holder"
                value={holder}
                onChange={(e) => {
                  setHolder(e.target.value);
                }}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="balance" className="form-label">
                {t("initialBalance")}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                id="balance"
                value={balance}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || (!isNaN(value) && parseFloat(value) >= 0)) {
                    setBalance(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
                    e.preventDefault();
                  }
                }}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-cancel" onClick={handleCancel}>
              {t("cancel", "Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!cardId.trim() || loading}
            >
              {loading ? t("saving", "Saving...") : t("save", "Save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddEditCardModal;
